import { AlertTriangle, ChevronRight } from "lucide-react";
import React from "react";

import type {
  BulkEditPreview,
  BulkEditPreviewSummaryRow,
  BulkEditPreviewVisitRow,
} from "./utils/bulkEditForm";

export interface VisitsBulkEditPreviewViewProps {
  preview: BulkEditPreview;
}

function SummaryChangeCell({ row }: { row: BulkEditPreviewSummaryRow }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-neutral-500">{row.fromLabel}</span>
      <span className="text-neutral-400">→</span>
      <span className="inline-flex rounded-full bg-diff-added-bg px-2.5 py-0.5 text-sm font-medium text-diff-added-text">
        {row.toLabel}
      </span>
    </div>
  );
}

function VisitChangeCell({ row }: { row: BulkEditPreviewVisitRow }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-neutral-500">{row.fromLabel}</span>
      <span className="text-neutral-400">→</span>
      <span className="inline-flex rounded-full bg-diff-added-bg px-2 py-0.5 text-xs font-medium text-diff-added-text">
        {row.toLabel}
      </span>
    </div>
  );
}

export const VisitsBulkEditPreviewView: React.FC<VisitsBulkEditPreviewViewProps> = ({
  preview,
}) => {
  const [visitsSectionOpen, setVisitsSectionOpen] = React.useState(true);
  const [expandedVisitIds, setExpandedVisitIds] = React.useState<Set<string>>(() => new Set());

  const allExpanded = preview.visits.length > 0 && expandedVisitIds.size === preview.visits.length;

  const handleExpandAll = () => {
    if (allExpanded) {
      setExpandedVisitIds(new Set());
      return;
    }
    setExpandedVisitIds(new Set(preview.visits.map((visit) => visit.visitId)));
  };

  const toggleVisit = (visitId: string) => {
    setExpandedVisitIds((prev) => {
      const next = new Set(prev);
      if (next.has(visitId)) {
        next.delete(visitId);
      } else {
        next.add(visitId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-5 px-6 py-5">
      <p className="text-sm text-neutral-500">
        You&apos;re about to apply changes to {preview.visitCount} visit
        {preview.visitCount !== 1 ? "s" : ""}.
      </p>

      {preview.replacesCitations && (
        <div className="flex gap-3 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3 text-sm text-warning-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" />
          <p>The new citations replace every existing citation on each updated visit.</p>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-neutral-900">Summary of changes</h4>
        <p className="mt-1 text-sm text-neutral-500">Only fields being changed are shown.</p>
        <div className="mt-3 overflow-hidden rounded-lg border border-neutral-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Field</th>
                <th className="px-4 py-3">Change</th>
                <th className="px-4 py-3 text-right">Visits affected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {preview.summaryRows.map((row) => (
                <tr key={row.field}>
                  <td className="px-4 py-3 font-medium text-neutral-900">{row.label}</td>
                  <td className="px-4 py-3">
                    <SummaryChangeCell row={row} />
                  </td>
                  <td className="px-4 py-3 text-right text-neutral-700">{row.visitsAffected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-neutral-200">
        <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-2">
          <button
            type="button"
            onClick={() => setVisitsSectionOpen((open) => !open)}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            <div
              className={`flex h-4 w-4 shrink-0 items-center justify-center text-neutral-500 transition-transform duration-200 ${visitsSectionOpen ? "rotate-90" : ""}`}
            >
              <ChevronRight size={12} strokeWidth={2.5} />
            </div>
            <div className="min-w-0 leading-tight">
              <p className="text-xs font-semibold text-neutral-900">
                View affected visits ({preview.visits.length})
              </p>
              <p className="text-2xs text-neutral-500">Review changes for each visit.</p>
            </div>
          </button>
          <button
            type="button"
            onClick={handleExpandAll}
            className="shrink-0 text-2xs font-medium text-primary hover:text-primary-hover"
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>

        {visitsSectionOpen && (
          <div className="divide-y divide-neutral-300">
            {preview.visits.map((visit) => {
              const isExpanded = expandedVisitIds.has(visit.visitId);
              return (
                <div
                  key={visit.visitId}
                  className={`${isExpanded ? "bg-neutral-50 shadow-inner" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleVisit(visit.visitId)}
                    className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-neutral-50`}
                  >
                    <div
                      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center text-neutral-500 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                    >
                      <ChevronRight size={11} strokeWidth={2.5} />
                    </div>
                    <span
                      className={`text-xs text-neutral-900 ${isExpanded ? "font-bold" : "font-medium"}`}
                    >
                      {visit.label}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="space-y-1 bg-neutral-50 px-3 py-1">
                      {visit.fields.map((row) => (
                        <div
                          key={row.field}
                          className="ml-6 grid grid-cols-[6rem_1fr] items-center gap-x-2 border-t border-neutral-200/30 pt-1 text-xs"
                        >
                          <span className="font-medium text-neutral-600">{row.label}</span>
                          <VisitChangeCell row={row} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
